import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import useIsWrapping from '../useIsWrapping';
import { getBorderRadius } from '../utils/borderRadiusUtils';

// Centralizing here since used by both ComboButton and ComboButton wrapper.
const sharedPropTypes = {
  /** Array of `LabelButton` or `Menu` elements to display
   *
   * The following props of these elements have special handling applied:
   *
   * | Prop | Description |
   * |------|-------|
   * | className | Amended with Forge class names  |
   * | borderRadius | Overwritten. Value is based on the position of the element within the buttons array |
   * | size | Overwritten with ComboButton prop |
   *
   */
  buttons: PropTypes.array,

  /** CSS class to apply to the root element */
  className: PropTypes.string,

  /** When true, automatically resize the buttons to large when they stack because they can't fit on a single line */
  largeWhenStacked: PropTypes.bool,

  /** Specify button size */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

const sharedDefaults = {
  largeWhenStacked: false,
};

ComboButton.propTypes = sharedPropTypes;
ComboButton.defaultProps = sharedDefaults;

/** Button with a multiple actions at the same level of emphasis. Can trigger a Menu, or work as a CTA with multiple related options. */
export default function ComboButton({ buttons, className, largeWhenStacked, size }) {
  const [activeButtonIndex, setActiveButtonIndex] = useState(null);
  const buttonRef = useRef([]);
  const stacked = useIsWrapping(buttonRef);

  return (
    <div
      role="group"
      className={cx(
        'fe_c_combo-button',
        { 'fe_c_combo-button--row': !stacked },
        { 'fe_c_combo-button--stacked': stacked },
        `fe_c_combo-button--${size}`,
        // Line above handles the explicitly set large size, so only handling the largeWhenStacked setting below.
        { 'fe_c_combo-button--large': size !== 'large' && stacked && largeWhenStacked },
        className
      )}
    >
      {buttons.map((button, index) => {
        const { key, ref } = button;
        const extraPropsToApply = {
          size,
          ref: (el) => {
            if (el) {
              buttonRef.current[index] = el;

              // Continue to forward the ref even though we've captured it.
              if (ref) {
                if (typeof ref === 'function') {
                  ref(el);
                } else if (typeof ref === 'object' && Object.prototype.hasOwnProperty.call(ref, 'current')) {
                  ref.current = el;
                }
              }
            }
          },
          // Used to set the parent div's z-index so all 4 border's of the active button are displayed.
          onFocus: (e) => {
            setActiveButtonIndex(index);
            if (button.props.onFocus) button.props.onFocus(e);
          },
          onClick: (e) => {
            setActiveButtonIndex(index);
            if (button.props.onClick) button.props.onClick(e);
          },
          borderRadius: getBorderRadius(index, buttons.length, stacked),
          className: cx(
            'fe_c_combo-button__button',
            `fe_c_combo-button__button--${stacked ? 'stacked' : 'row'}`,
            button.props.className
          ),
        };

        // If the element is a Menu, then apply the extra props to the Menu's trigger.
        // Otherwise, assume it's an LabelButton, so apply the extra props to the LabelButton.
        const element =
          button.type.displayName === 'Menu'
            ? React.cloneElement(button, {
                trigger: { ...button.props.trigger, props: { ...button.props.trigger.props, ...extraPropsToApply } },
              })
            : React.cloneElement(button, extraPropsToApply);

        return (
          <div
            key={key || index}
            className={cx('fe_c_combo-button__button-wrapper', {
              'fe_c_combo-button__button-wrapper--focused': index === activeButtonIndex,
            })}
            ref={(el) => {
              if (el) buttonRef.current[index] = el;
            }}
          >
            {element}
          </div>
        );
      })}
    </div>
  );
}
